import type { DebugBridge } from './DebugBridge';

export interface RegisterDisplay {
  name: string;
  value: number;
  formatted: string;
  changed: boolean;
  bits?: string;
}

const GENERAL_REGS = ['R0', 'R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R8', 'R9', 'R10', 'R11', 'R12', 'R13', 'R14', 'R15'];
const ER_REGS = ['ER0', 'ER2', 'ER4', 'ER6', 'ER8', 'ER10', 'ER12', 'ER14'];
const SPECIAL_REGS = ['PC', 'SP', 'PSW', 'CSR', 'EA', 'DSR'];

function hex(v: number, width: number = 2): string {
  return '0x' + v.toString(16).padStart(width, '0').toUpperCase();
}

function dec(v: number): string {
  return v.toString(10);
}

function bin(v: number, width: number = 4): string {
  const s = v.toString(2);
  if (width <= 4) return s.padStart(width * 4, '0');
  return s.padStart(width, '0');
}

function formatValue(value: number, radix: 16 | 10 | 2): string {
  switch (radix) {
    case 16: return hex(value, 8);
    case 10: return dec(value);
    case 2: return bin(value, 32);
  }
}

function getPSWBits(psw: number): string {
  const parts: string[] = [];
  if (psw & 0x8000) parts.push('N');
  if (psw & 0x4000) parts.push('Z');
  if (psw & 0x2000) parts.push('V');
  if (psw & 0x1000) parts.push('C');
  return parts.length > 0 ? parts.join(' ') : '---';
}

function getCSRBits(csr: number): string {
  const bank = (csr >> 3) & 0x7;
  return 'Bank:' + bank;
}

export class RegisterView {
  readonly el: HTMLDivElement;
  private bridge: DebugBridge;
  private registers: RegisterDisplay[] = [];
  private radix: 16 | 10 | 2 = 16;
  private prevValues: Map<string, number> = new Map();
  private tableEl: HTMLTableElement;

  constructor(bridge: DebugBridge) {
    this.bridge = bridge;

    this.el = document.createElement('div');
    this.el.className = 'register-view';

    const toolbar = document.createElement('div');
    toolbar.className = 'register-toolbar';

    const hexBtn = document.createElement('button');
    hexBtn.textContent = 'HEX';
    hexBtn.className = 'register-radix-btn active';
    hexBtn.onclick = () => this.setRadix(16);
    toolbar.appendChild(hexBtn);

    const decBtn = document.createElement('button');
    decBtn.textContent = 'DEC';
    decBtn.className = 'register-radix-btn';
    decBtn.onclick = () => this.setRadix(10);
    toolbar.appendChild(decBtn);

    const binBtn = document.createElement('button');
    binBtn.textContent = 'BIN';
    binBtn.className = 'register-radix-btn';
    binBtn.onclick = () => this.setRadix(2);
    toolbar.appendChild(binBtn);

    const refreshBtn = document.createElement('button');
    refreshBtn.textContent = 'Refresh';
    refreshBtn.className = 'register-refresh-btn';
    refreshBtn.onclick = () => { void this.refresh(); };
    toolbar.appendChild(refreshBtn);

    this.el.appendChild(toolbar);

    this.tableEl = document.createElement('table');
    this.tableEl.className = 'register-table';
    this.el.appendChild(this.tableEl);
  }

  setRadix(radix: 16 | 10 | 2): void {
    this.radix = radix;
    this.rebuildDisplay();
    this.update();
    const btns = this.el.querySelectorAll('.register-radix-btn');
    btns.forEach(b => b.classList.remove('active'));
    const labels: Record<number, string> = { 16: 'HEX', 10: 'DEC', 2: 'BIN' };
    btns.forEach(b => {
      if (b.textContent === labels[radix]) b.classList.add('active');
    });
  }

  async refresh(): Promise<void> {
    const regs = await this.bridge.getRegisters();

    const raw: Record<string, number> = {};
    for (let i = 0; i < 16; i++) {
      raw[`R${i}`] = regs.r[i];
    }
    for (let i = 0; i < 16; i += 2) {
      raw[`ER${i}`] = ((regs.r[i] << 8) | regs.r[i + 1]) >>> 0;
    }
    raw['PC'] = regs.pc;
    raw['SP'] = regs.sp;
    raw['PSW'] = regs.psw;
    raw['CSR'] = regs.csr;
    raw['EA'] = regs.ea;
    raw['DSR'] = regs.dsr;

    this.registers = [];

    const makeDisplay = (name: string, value: number, isSpecial: boolean): RegisterDisplay => {
      const changed = this.prevValues.has(name) && this.prevValues.get(name) !== value;
      this.prevValues.set(name, value);
      const display: RegisterDisplay = {
        name,
        value,
        formatted: formatValue(value, this.radix),
        changed,
      };
      if (isSpecial) {
        if (name === 'PSW') {
          display.bits = getPSWBits(value);
        } else if (name === 'CSR') {
          display.bits = getCSRBits(value);
        } else {
          display.bits = bin(value, 8);
        }
      }
      return display;
    };

    for (const name of GENERAL_REGS) {
      this.registers.push(makeDisplay(name, raw[name], false));
    }
    for (const name of ER_REGS) {
      this.registers.push(makeDisplay(name, raw[name], false));
    }
    for (const name of SPECIAL_REGS) {
      this.registers.push(makeDisplay(name, raw[name], true));
    }

    this.update();
  }

  private rebuildDisplay(): void {
    for (const reg of this.registers) {
      reg.formatted = formatValue(reg.value, this.radix);
    }
  }

  render(): HTMLElement {
    this.update();
    return this.el;
  }

  update(): void {
    this.tableEl.innerHTML = '';

    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    for (const h of ['Name', 'Value', 'Bits']) {
      const th = document.createElement('th');
      th.textContent = h;
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    this.tableEl.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const reg of this.registers) {
      const tr = document.createElement('tr');
      tr.className = 'register-row';
      if (reg.changed) tr.classList.add('register-changed');

      const tdName = document.createElement('td');
      tdName.className = 'register-name';
      tdName.textContent = reg.name;
      tr.appendChild(tdName);

      const tdValue = document.createElement('td');
      tdValue.className = 'register-value';
      tdValue.textContent = reg.formatted;
      tr.appendChild(tdValue);

      const tdBits = document.createElement('td');
      tdBits.className = 'register-bits';
      tdBits.textContent = reg.bits ?? '';
      tr.appendChild(tdBits);

      tbody.appendChild(tr);
    }
    this.tableEl.appendChild(tbody);
  }

  destroy(): void {
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
